import { useEffect, useState } from 'react';
import { RiArrowRightLine, RiHomeSmile2Line, RiToolsLine } from 'react-icons/ri';
import { useCatalogType } from '../contexts/CatalogTypeContext';
import { getAssetPath } from '../utils/paths';
import '../styles/CatalogTypeSelector.css';

const TYPE_ICONS = {
  lights_living: RiHomeSmile2Line,
  heavy_machine: RiToolsLine
};

const preloadBackground = (url) => new Promise((resolve) => {
  if (!url || typeof window === 'undefined') {
    resolve();
    return;
  }

  const image = new Image();
  image.onload = resolve;
  image.onerror = resolve;
  image.src = url;
});

export default function CatalogTypeSelector({ onSelect }) {
  const { catalogOptions, catalogSettings, catalogSettingsLoaded } = useCatalogType();
  const [backgroundReady, setBackgroundReady] = useState(false);

  useEffect(() => {
    let active = true;
    setBackgroundReady(false);

    const loadBackground = async () => {
      await preloadBackground(catalogSettings.background_image);
      if (active) setBackgroundReady(true);
    };

    if (catalogSettingsLoaded) {
      loadBackground();
    }

    return () => {
      active = false;
    };
  }, [catalogSettings.background_image, catalogSettingsLoaded]);

  if (!catalogSettingsLoaded || !backgroundReady) {
    return <main className="catalog-selector catalog-selector--loading" aria-busy="true" />;
  }

  const logoVariant = catalogSettings.logo_variant === 'logo_white' ? 'logo_white' : 'logo_color';
  const logo = getAssetPath(`logos/${logoVariant}.png`);

  return (
    <main
      className="catalog-selector"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.36), rgba(0, 0, 0, 0.62)), url(${catalogSettings.background_image})`
      }}
    >
      <div className="catalog-selector-shell">
        <div className="catalog-selector-brand">
          <img className="catalog-selector-logo" src={logo} alt="Building Depot" />
          <div className="catalog-selector-kicker">{catalogSettings.kicker}</div>
          <h1>{catalogSettings.title}</h1>
        </div>

        <div className="catalog-selector-options">
          {catalogOptions.map((type) => {
            const Icon = TYPE_ICONS[type.id] || RiToolsLine;

            return (
              <button
                key={type.id}
                type="button"
                className={`catalog-selector-option catalog-selector-option--${type.id}`}
                style={{ '--option-primary': type.primary_color }}
                onClick={() => onSelect(type.id)}
              >
                <span className="catalog-selector-icon">
                  <Icon />
                </span>
                <span className="catalog-selector-text">
                  <span className="catalog-selector-title">{type.label}</span>
                  <span className="catalog-selector-description">{type.description}</span>
                </span>
                <RiArrowRightLine className="catalog-selector-arrow" />
              </button>
            );
          })}
        </div>
      </div>
    </main>
  );
}
